import React, { memo } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  StyleProps,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useFlatFolderList } from '@core/folders/folders-api'
import FoldersTreeAutocomplete from '@core/folders/folders-autocomplete'
import { FolderWithParent } from '@pages/my-folders/my-folders-utils'

import { useProjectsState } from './projects-state'

const styles = {
  paper: {
    backgroundColor: (theme) => theme.palette.color.N020,
    color: (theme) => theme.palette.color.N800,
    boxShadow: '0 0 1px 0 #091e424f',
  },

  dialogText: {
    fontSize: '0.875rem',
    lineHeight: 1.79,
    letterSpacing: '-0.09px',
    fontWeight: 600,
  },
  folderLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: (theme) => theme.palette.color.N700,
  },
  folderPath: {
    fontSize: '1rem',
    fontWeight: 500,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1.56,
    letterSpacing: '-0.1px',
  },
} satisfies StyleProps

type ProjectSaveItemContentProps = {
  isSubmitting: boolean
  onSubmit: (folder: FolderWithParent) => Promise<void>
  onClose: () => void
}

const ProjectSaveItemContent = (props: ProjectSaveItemContentProps) => {
  const { t } = useTranslation('common')
  const openCreateProject = useProjectsState((state) => state.openCreateProject)
  const { selectedProjectId, currentProjectId, mode } = useProjectsState((state) => state.save)
  const foldersQuery = useFlatFolderList()
  const confirmButtonLabel = t('projects.confirm_save_item')

  const folders = foldersQuery.data ?? []

  const handleChange = (folderId: string) => {
    useProjectsState.setState((prevState) => ({
      save: { ...prevState.save, selectedProjectId: folderId },
    }))
  }

  const selectedFolder = React.useMemo(
    () => folders.find((folder) => folder.id === selectedProjectId) || null,
    [folders, selectedProjectId],
  )

  const handleCreate = () => {
    openCreateProject((project) => {
      if (!project) return

      handleChange(project.id)

      // focus the confirm button after creating a project
      setTimeout(() => {
        const confirmButton = document.querySelector(
          `button[aria-label="${confirmButtonLabel}"]`,
        ) as HTMLButtonElement
        confirmButton?.focus()
      }, 500)
    })
  }

  const title =
    mode === 'move' ? t('projects.item_will_be_moved') : t('projects.item_will_be_saved')
  const dialogComponent = (
    <>
      {mode === 'move'
        ? t('projects.ensure_moving_to_folder')
        : t('projects.ensure_saving_to_folder')}{' '}
      <Link href="#" color="secondary" onClick={handleCreate}>
        {t('projects.create_folder')}
      </Link>
      {mode === 'move' ? ` ${t('projects.in_top_level')}` : '.'}
    </>
  )

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault()

        if (!selectedFolder) return

        props.onSubmit(selectedFolder)
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box typography="h3" component="span" sx={{ flexGrow: 1 }}>
          {title}
        </Box>

        <IconButton
          sx={{ mr: 0, flexShrink: 0 }}
          edge="end"
          aria-label={t('accessibility.close')}
          disabled={props.isSubmitting}
          onClick={props.onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflowY: 'initial' }}>
        <Paper elevation={0} sx={{ mb: 2, ...styles.paper }}>
          <Box sx={styles.dialogText}>{dialogComponent}</Box>
          <Box mt={4}>
            <FoldersTreeAutocomplete
              folders={foldersQuery.data}
              value={selectedFolder}
              onChange={(folder) => handleChange((folder as FolderWithParent | null)?.id || '')}
              mode={mode}
              currentFolderId={currentProjectId}
            />
          </Box>
        </Paper>

        <Paper elevation={0} sx={styles.paper}>
          <Typography sx={{ mb: 2, ...styles.folderLabel }}>
            {t('projects.folder_destination')}
          </Typography>

          <Typography sx={{ wordBreak: 'break-word', ...styles.folderPath }}>
            {t('projects.my_folders')} /{' '}
            {selectedFolder?.breadcrumbs.map((b) => b.name).join(' / ') || ''}
          </Typography>
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose} color="secondary" disabled={props.isSubmitting}>
          {t('actions.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="secondary"
          type="submit"
          disabled={props.isSubmitting || !selectedFolder?.permissions?.write}
          aria-label={confirmButtonLabel}
          loading={props.isSubmitting}
        >
          {t('actions.confirm')}
        </LoadingButton>
      </DialogActions>
    </Box>
  )
}

const ProjectSaveItemDialog = () => {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const onCloseSaveItem = useProjectsState((state) => state.onCloseSaveItem)

  const { onClose } = useProjectsState((state) => state.save)
  const isOpen = useProjectsState((state) => !state.edit.open && state.save.open)

  const handleClose = () => {
    onCloseSaveItem()

    if (onClose) {
      onClose({
        project: null,
        reason: 'cancel',
      })
    }
  }

  const onSubmit: ProjectSaveItemContentProps['onSubmit'] = async (folder) => {
    if (!onClose) return

    setIsSubmitting(true)

    try {
      await onClose({
        project: folder,
        reason: 'confirm',
      })
      onCloseSaveItem()
    } catch (error) {
      console.error('Failed to save item to project', error)
      // nothing to do here
    }

    setIsSubmitting(false)
  }

  return (
    <Dialog
      open={isOpen}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="xs"
      fullWidth
      sx={{ '& .MuiPaper-root': { maxWidth: '596px', px: 1.5, py: 2 } }}
    >
      <React.Suspense fallback={<LinearProgress variant="indeterminate" color="info" />}>
        <ProjectSaveItemContent
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          onClose={handleClose}
        />
      </React.Suspense>
    </Dialog>
  )
}

export default memo(ProjectSaveItemDialog)
